import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Award, Download, FileText, History, Loader2, Receipt, Ticket } from "lucide-react";

const fmtEur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

const downloadBlob = async (url, filename, opts = {}) => {
  const token = localStorage.getItem("token");
  const res = await fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return res;
};

/**
 * AplActionsPanel — admin actions for APL and TK users.
 *
 * Both special roles bypass the automatic diploma/invoice pipeline and
 * require admin-driven document emission. Rendered inside the "Cursos del
 * usuario" dialog when ``user.is_apl === true`` or ``user.is_tk === true``.
 *
 * APL variant  → Generar factura · Histórico · Generar diploma/certificado
 * TK variant   → Generar ticket · Histórico · Generar diploma/certificado
 *
 * The history endpoint (`/admin/users/{id}/invoices`) already returns both
 * invoices and tickets tagged with ``document_kind`` — the history modal
 * labels each row accordingly.
 *
 * Props:
 *   user          — { id, first_name, last_name, email, dni, is_apl, is_tk, ... }
 *   courses       — [{ test_id, title, kind, passed, final_score, ... }]
 */
export default function AplActionsPanel({ user, courses }) {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const perms = currentUser?.perms || {};
  const isTk = !!user?.is_tk;
  const canAct = isSuperadmin || (isTk ? !!perms.tk_actions : !!perms.apl_actions);
  const badgeClass = isTk
    ? "bg-sky-200 text-sky-900 border-sky-300"
    : "bg-amber-200 text-amber-900 border-amber-300";
  const cardClass = isTk
    ? "p-4 border border-sky-200 bg-sky-50/40 rounded-lg space-y-3"
    : "p-4 border border-amber-200 bg-amber-50/40 rounded-lg space-y-3";
  const titleColor = isTk ? "text-sky-900" : "text-amber-900";
  const noticeColor = isTk ? "text-sky-800/80" : "text-amber-800/80";
  const label = isTk ? "TK" : "APL";
  const notice = isTk
    ? "Este alumno NO recibe facturas ni diplomas automáticos. Se emiten tickets manualmente."
    : "Este alumno NO recibe facturas ni diplomas automáticos. Emítelos desde aquí.";

  if (!canAct) {
    return (
      <Card
        className={cardClass}
        data-testid="apl-actions-panel-locked"
      >
        <div className={`flex items-center gap-2 ${titleColor}`}>
          <span className={`inline-flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 rounded border ${badgeClass}`}>
            {label}
          </span>
          <p className="text-sm font-semibold">Acciones manuales para este alumno</p>
        </div>
        <p className={`text-xs ${noticeColor}`}>
          No tienes permiso para gestionar acciones manuales de usuarios {label}. Solicítalo a un superadmin.
        </p>
      </Card>
    );
  }

  return (
    <Card
      className={cardClass}
      data-testid="apl-actions-panel"
    >
      <div className={`flex items-center gap-2 ${titleColor}`}>
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 rounded border ${badgeClass}`}>
          {label}
        </span>
        <p className="text-sm font-semibold">Acciones manuales para este alumno</p>
      </div>
      <p className={`text-xs ${noticeColor}`}>{notice}</p>
      <div className="flex flex-wrap gap-2">
        {isTk ? <ManualTicketButton user={user} /> : <ManualInvoiceButton user={user} />}
        <InvoiceHistoryButton user={user} isTk={isTk} />
        <ManualCertificateButton user={user} courses={courses} />
      </div>
    </Card>
  );
}

/* -------------------- 1) Manual invoice -------------------- */
function ManualInvoiceButton({ user }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    amount: "", concept: "",
    first_name: "", last_name: "", email: "", tax_id: "",
    address: "", postal_code: "", city: "", country: "ES",
  });

  useEffect(() => {
    if (open) {
      setForm({
        amount: "", concept: "",
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        tax_id: user?.dni || "",
        address: user?.billing?.address || "",
        postal_code: user?.billing?.postal_code || "",
        city: user?.billing?.city || "",
        country: user?.billing?.country || "ES",
      });
    }
  }, [open, user]);

  const submit = async () => {
    const amount = parseFloat((form.amount || "").toString().replace(",", "."));
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error("Importe inválido");
      return;
    }
    if (!form.concept.trim()) {
      toast.error("Introduce el concepto de la factura");
      return;
    }
    setSaving(true);
    try {
      const res = await downloadBlob(
        `${API}/admin/users/${user.id}/manual-invoice`,
        "factura.pdf",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount,
            concept: form.concept.trim(),
            customer: {
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              email: form.email.trim().toLowerCase(),
              tax_id: form.tax_id.trim(),
              address: form.address.trim(),
              city: form.city.trim(),
              postal_code: form.postal_code.trim(),
              country: form.country.trim() || "ES",
            },
          }),
        },
      );
      const invNo = res.headers.get("X-Invoice-Number");
      toast.success(`Factura ${invNo || ""} generada`);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "No se pudo generar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="bg-white"
        data-testid="apl-open-manual-invoice"
      >
        <Receipt className="w-4 h-4 mr-1" />
        Generar factura
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="apl-manual-invoice-modal">
          <DialogHeader>
            <DialogTitle>Emitir factura manual</DialogTitle>
            <DialogDescription>
              Numeración correlativa con las facturas online. Los datos del alumno se han
              autocompletado — puedes ajustarlos antes de emitir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mi-amount">Importe (EUR) *</Label>
                <Input
                  id="mi-amount"
                  type="text"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="250,00"
                  data-testid="apl-mi-amount"
                />
              </div>
              <div>
                <Label htmlFor="mi-tax">CIF / NIF / NIE</Label>
                <Input
                  id="mi-tax"
                  value={form.tax_id}
                  onChange={(e) => setForm({ ...form, tax_id: e.target.value.toUpperCase() })}
                  data-testid="apl-mi-tax"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="mi-concept">Concepto *</Label>
              <Textarea
                id="mi-concept"
                rows={2}
                value={form.concept}
                onChange={(e) => setForm({ ...form, concept: e.target.value })}
                placeholder="Ej. Formación presencial en atención sociosanitaria · Junio 2026"
                data-testid="apl-mi-concept"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mi-fn">Nombre</Label>
                <Input
                  id="mi-fn"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="apl-mi-first-name"
                />
              </div>
              <div>
                <Label htmlFor="mi-ln">Apellidos</Label>
                <Input
                  id="mi-ln"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="apl-mi-last-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="mi-email">Email</Label>
              <Input
                id="mi-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="apl-mi-email"
              />
            </div>
            <div>
              <Label htmlFor="mi-address">Dirección</Label>
              <Input
                id="mi-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="apl-mi-address"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="mi-cp">C.P.</Label>
                <Input
                  id="mi-cp"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="mi-city">Ciudad</Label>
                <Input
                  id="mi-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="apl-mi-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Generar y descargar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- 2) Invoice history -------------------- */
/* -------------------- 1b) Manual ticket (TK users) -------------------- */
function ManualTicketButton({ user }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ amount: "", concept: "" });
  const [issuedNumber, setIssuedNumber] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ amount: "", concept: "" });
      setIssuedNumber(null);
    }
  }, [open]);

  const submit = async () => {
    const amount = parseFloat((form.amount || "").toString().replace(",", "."));
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error("Importe inválido");
      return;
    }
    if (!form.concept.trim()) {
      toast.error("Introduce el concepto del ticket");
      return;
    }
    setSaving(true);
    try {
      const res = await downloadBlob(
        `${API}/admin/users/${user.id}/manual-ticket`,
        "ticket.pdf",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount,
            concept: form.concept.trim(),
          }),
        },
      );
      const tkNo = res.headers.get("X-Ticket-Number");
      setIssuedNumber(tkNo);
      toast.success(`Ticket ${tkNo || ""} emitido`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-sky-600 hover:bg-sky-700 text-white"
        data-testid="tk-open-manual-ticket"
      >
        <Ticket className="w-4 h-4 mr-1" />
        Generar ticket
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="tk-manual-ticket-modal">
          <DialogHeader>
            <DialogTitle>Emitir ticket</DialogTitle>
            <DialogDescription>
              El ticket usa los datos de facturación configurados en Ajustes (empresa, CIF,
              dirección, IVA) y no incluye datos del destinatario. La numeración es correlativa
              propia (T-YYYY-NNNNN), independiente de las facturas.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Importe (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))}
                  placeholder="25,00"
                  data-testid="tk-ticket-amount"
                />
              </div>
              <div className="flex items-end text-xs text-gray-500">
                Base imponible. El IVA se aplica según Ajustes.
              </div>
            </div>
            <div>
              <Label className="text-xs">Concepto *</Label>
              <Textarea
                rows={2}
                value={form.concept}
                onChange={(e) => setForm((s) => ({ ...s, concept: e.target.value }))}
                placeholder="Curso presencial de primeros auxilios · Enero 2026"
                data-testid="tk-ticket-concept"
              />
            </div>
            {issuedNumber && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                Ticket {issuedNumber} emitido correctamente. La descarga se ha iniciado.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cerrar</Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="tk-submit-manual-ticket"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Emitiendo…</> : <><Ticket className="w-4 h-4 mr-1" /> Emitir</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/* -------------------- 2) Invoice / ticket history -------------------- */
function InvoiceHistoryButton({ user, isTk = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    api.get(`/admin/users/${user.id}/invoices`)
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("No se pudo cargar el histórico"));
  }, [open, user.id]);

  const download = async (invoiceId, invoiceNo, isTicket) => {
    try {
      const prefix = isTicket ? "ticket" : "factura";
      await downloadBlob(
        `${API}/admin/invoices/${invoiceId}/pdf`,
        `${prefix}-${invoiceNo || invoiceId}.pdf`,
      );
    } catch (e) { toast.error(e.message); }
  };

  const buttonLabel = isTk ? "Histórico documentos" : "Histórico facturas";
  const modalTitle = isTk ? "Histórico de documentos" : "Histórico de facturas";
  const modalDescription = isTk
    ? "Facturas y tickets emitidos al alumno, tanto online como manuales."
    : "Todas las facturas emitidas al alumno, tanto compras online como manuales.";
  const emptyMsg = isTk
    ? "Este alumno todavía no tiene documentos emitidos."
    : "Este alumno todavía no tiene facturas emitidas.";
  const totalPrefix = isTk ? "Total emitido" : "Total facturado";

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="bg-white"
        data-testid="apl-open-invoice-history"
      >
        <History className="w-4 h-4 mr-1" />
        {buttonLabel}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="apl-invoice-history-modal">
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
            <DialogDescription>{modalDescription}</DialogDescription>
          </DialogHeader>
          {items === null ? (
            <p className="text-sm text-gray-500 py-6 text-center">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="apl-invoice-history-empty">
              {emptyMsg}
            </p>
          ) : (
            <table className="w-full text-sm mt-2" data-testid="apl-invoice-history-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b">
                  <th className="text-left py-2">Nº</th>
                  <th className="text-left py-2">Documento</th>
                  <th className="text-left py-2">Fecha</th>
                  <th className="text-left py-2">Concepto</th>
                  <th className="text-right py-2">Importe</th>
                  <th className="text-right py-2 pl-2">Origen</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const isTicket = it.document_kind === "ticket";
                  return (
                    <tr key={it.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`apl-invoice-row-${it.id}`}>
                      <td className="py-2 font-mono-data text-brand-ink">{it.invoice_number || "—"}</td>
                      <td className="py-2">
                        {isTicket ? (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">TICKET</span>
                        ) : (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-brand-soft text-brand-ink border border-[#bcdfd9]">FACTURA</span>
                        )}
                      </td>
                      <td className="py-2 text-gray-600">{fmtDate(it.paid_at || it.created_at)}</td>
                      <td className="py-2 max-w-xs truncate" title={it.concept}>{it.concept}</td>
                      <td className="py-2 text-right font-mono-data">{fmtEur(it.amount_eur)}</td>
                      <td className="py-2 text-right">
                        {it.is_manual ? (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">manual</span>
                        ) : (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-gray-50 text-gray-700 border border-gray-200">online</span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => download(it.id, it.invoice_number, isTicket)}
                          data-testid={`apl-invoice-download-${it.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50" data-testid="apl-invoice-history-total-row">
                  <td colSpan="4" className="py-3 text-right text-[11px] uppercase tracking-wider text-gray-600 font-mono-data">
                    {totalPrefix} ({items.length}) ·
                  </td>
                  <td className="py-3 text-right font-display text-lg text-brand-ink" data-testid="apl-invoice-history-total">
                    {fmtEur(items.reduce((acc, it) => acc + (Number(it.amount_eur) || 0), 0))}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- 3) Manual certificate / diploma -------------------- */
function ManualCertificateButton({ user, courses }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    test_id: "",
    score_mode: "auto",
    manual_score: "",
  });

  useEffect(() => {
    if (open && courses && courses.length > 0 && !form.test_id) {
      setForm((f) => ({ ...f, test_id: courses[0].test_id }));
    }
  }, [open, courses, form.test_id]);

  const submit = async () => {
    if (!form.test_id) {
      toast.error("Selecciona un curso");
      return;
    }
    let manualScore = null;
    if (form.score_mode === "manual") {
      const s = parseFloat((form.manual_score || "").toString().replace(",", "."));
      if (Number.isNaN(s) || s < 0 || s > 100) {
        toast.error("Puntuación entre 0 y 100");
        return;
      }
      manualScore = s;
    }
    setSaving(true);
    try {
      const selected = (courses || []).find((c) => c.test_id === form.test_id);
      const filename = `${selected?.kind === "final" ? "diploma" : "certificado"}-${(selected?.title || "curso").replace(/\s+/g, "_")}.pdf`;
      await downloadBlob(
        `${API}/admin/users/${user.id}/manual-certificate`,
        filename,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            test_id: form.test_id,
            score_mode: form.score_mode,
            manual_score: manualScore,
          }),
        },
      );
      toast.success("Documento generado");
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "No se pudo generar");
    } finally {
      setSaving(false);
    }
  };

  const selected = (courses || []).find((c) => c.test_id === form.test_id);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="bg-white"
        data-testid="apl-open-manual-certificate"
      >
        <Award className="w-4 h-4 mr-1" />
        Generar diploma / certificado
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="apl-manual-cert-modal">
          <DialogHeader>
            <DialogTitle>Emitir diploma o certificado</DialogTitle>
            <DialogDescription>
              El tipo de documento se decide por el curso: los cursos marcados como
              &quot;Curso / Taller&quot; emiten diploma; el resto emiten certificado.
            </DialogDescription>
          </DialogHeader>
          {!courses || courses.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              Este alumno no tiene cursos asignados.
            </p>
          ) : (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Curso *</Label>
                <Select
                  value={form.test_id}
                  onValueChange={(v) => setForm({ ...form, test_id: v })}
                >
                  <SelectTrigger data-testid="apl-cert-course">
                    <SelectValue placeholder="Selecciona un curso" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => (
                      <SelectItem key={c.test_id} value={c.test_id}>
                        {c.kind === "final" ? "🎓" : "📜"} {c.title}
                        {c.passed ? ` · Aprobado (${c.final_score ?? "—"}%)` : c.attempts > 0 ? " · En progreso" : " · Sin intentos"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Puntuación</Label>
                <RadioGroup
                  value={form.score_mode}
                  onValueChange={(v) => setForm({ ...form, score_mode: v })}
                  className="mt-2 space-y-2"
                >
                  <label className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                    <RadioGroupItem value="auto" data-testid="apl-cert-score-auto" />
                    <div className="text-sm">
                      <p className="font-medium text-brand-ink">Usar puntuación del último intento aprobado</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {selected?.passed
                          ? `Se usará ${selected.final_score ?? "—"}%.`
                          : "El alumno no tiene ningún intento aprobado — esta opción fallará. Usa la puntuación manual."}
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                    <RadioGroupItem value="manual" data-testid="apl-cert-score-manual" />
                    <div className="text-sm flex-1">
                      <p className="font-medium text-brand-ink">Puntuación manual</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Para formación presencial sin test online, o para sobrescribir la nota.
                      </p>
                      {form.score_mode === "manual" && (
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={form.manual_score}
                          onChange={(e) => setForm({ ...form, manual_score: e.target.value })}
                          placeholder="Ej. 8,5 o 85"
                          className="mt-2 max-w-[140px]"
                          data-testid="apl-cert-manual-score"
                        />
                      )}
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </div>
          )}
          <DialogFooter className="mt-3">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
            <Button
              onClick={submit}
              disabled={saving || !form.test_id}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="apl-cert-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Emitir documento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
